import { useEffect, useState, type CSSProperties } from "react";
import {
  cornerEdges,
  cornerFor,
  type PictureCorner,
  type PictureSurface,
} from "@shared/tv-corners";

/**
 * "This screen is showing saved content" - the honest, quiet version.
 *
 * The brief is a contradiction on purpose: staff must be able to tell at a
 * glance that a room is running on its last-good config, and a guest must not
 * be made to feel they are looking at a broken television. So the badge has two
 * states:
 *
 *  - EXPANDED for {@link REVEAL_MS} after the screen goes offline. That moment
 *    is when somebody is most likely standing in front of it - the TV was just
 *    switched on, or the network just dropped while it was being used - and it
 *    is the only moment the message is worth words.
 *  - COLLAPSED forever after: a single small amber dot, which reads as a
 *    hardware LED to anyone who does not know what it means and as "this room
 *    is out of contact" to anyone who does.
 *
 * It never covers content, never animates aggressively, and never appears at
 * all while the screen is merely loading - only once we are knowingly rendering
 * a cached config against a failing API.
 */

/** How long the labelled form stays up before collapsing to the dot. */
const REVEAL_MS = 12_000;

const STRINGS: Record<
  string,
  { offline: string; since: (t: string) => string }
> = {
  sv: {
    offline: "Offline – visar sparat innehåll",
    since: (t) => `Senast uppdaterad ${t}`,
  },
  en: {
    offline: "Offline – showing saved content",
    since: (t) => `Last updated ${t}`,
  },
  nb: {
    offline: "Frakoblet – viser lagret innhold",
    since: (t) => `Sist oppdatert ${t}`,
  },
  da: {
    offline: "Offline – viser gemt indhold",
    since: (t) => `Senest opdateret ${t}`,
  },
  fi: {
    offline: "Offline – näytetään tallennettu sisältö",
    since: (t) => `Päivitetty viimeksi ${t}`,
  },
  de: {
    offline: "Offline – gespeicherte Inhalte",
    since: (t) => `Zuletzt aktualisiert ${t}`,
  },
};

function strings(locale?: string) {
  const key = (locale || "en").slice(0, 2).toLowerCase();
  return STRINGS[key === "no" ? "nb" : key] ?? STRINGS.en;
}

/** `savedAt` as a wall-clock the person in the room can compare to theirs. */
export function formatSavedAt(savedAt: number | null): string | null {
  if (!savedAt || !Number.isFinite(savedAt)) return null;
  const d = new Date(savedAt);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, "0");
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = `${p(d.getHours())}:${p(d.getMinutes())}`;
  return sameDay ? time : `${p(d.getDate())}/${p(d.getMonth() + 1)} ${time}`;
}

export interface OfflineBadgeProps {
  /** Whether the screen is knowingly rendering a cached config. */
  offline: boolean;
  /** When that cached config was last confirmed by the API (ms epoch). */
  savedAt?: number | null;
  /** Premise locale, for the twelve seconds the badge uses words. */
  locale?: string;
  /**
   * Position against the viewport rather than the letterboxed 16:9 picture.
   * Used by the door-sign surface, which owns its own root element.
   */
  fixed?: boolean;
  /**
   * Which surface's corner authority grants this badge its corner
   * (`@shared/tv-corners`). `screen-state` is rank 1 and is therefore never
   * displaced on any of them - but its corner is the TABLE's to state, not
   * this stylesheet's, which is what stops the next claimant picking it too.
   */
  surface?: PictureSurface;
}

export default function OfflineBadge({
  offline,
  savedAt = null,
  locale,
  fixed = false,
  surface = "scheme",
}: OfflineBadgeProps) {
  const [expanded, setExpanded] = useState(false);

  // Expand on every transition INTO offline, not once per mount: a room that
  // drops out twice in an evening should say so twice.
  useEffect(() => {
    if (!offline) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    const t = setTimeout(() => setExpanded(false), REVEAL_MS);
    return () => clearTimeout(t);
  }, [offline]);

  if (!offline) return null;

  const corner = cornerFor(surface, "screen-state");
  if (!corner) return null;

  const t = strings(locale);
  const when = formatSavedAt(savedAt);
  const label = when ? `${t.offline} · ${t.since(when)}` : t.offline;

  return (
    <div
      data-testid="offline-badge"
      data-expanded={expanded ? "1" : "0"}
      data-corner={corner}
      role="status"
      aria-label={label}
      title={label}
      style={{
        ...anchor(corner, fixed),
        ...(expanded ? expandedWrap : collapsedWrap),
      }}
    >
      <span aria-hidden="true" style={dot} />
      {expanded && <span style={text}>{label}</span>}
    </div>
  );
}

// The inset was `1.4cqw` against `.foyer-app`. Container queries are Chromium
// 105 and the Tizen 7 floor is Chromium 94, so on a Samsung panel those two
// declarations are INVALID and get dropped - the badge keeps `position:
// absolute` and loses its offsets, pinning it to the top-left corner.
//
// This is the exact same length, not an approximation. `.foyer-app` is
// `width: min(100vw, calc(100vh * 16 / 9))` with `box-sizing: border-box` and
// no padding, so its content box IS that width and 1.4% of it distributes
// over the min() unchanged. Philips renders identically before and after;
// Samsung now renders the same as Philips, which is the whole point.
//
// Keep the two in step: if `.foyer-app`'s width formula changes, this changes.
const APP_INSET = "min(1.4vw, calc(1.4vh * 16 / 9))";

/**
 * The same inset, applied to whichever two edges the corner authority granted.
 *
 * `fixed` still chooses the positioning CONTEXT and the inset (a surface that
 * owns its own root has no `.foyer-app` box to measure against); the CORNER is
 * not this function's to pick - it arrives already decided.
 */
function anchor(corner: PictureCorner, fixed: boolean): CSSProperties {
  const { x, y } = cornerEdges(corner);
  const inset = fixed ? 20 : APP_INSET;
  return {
    position: fixed ? "fixed" : "absolute",
    zIndex: 9998,
    [x]: inset,
    [y]: inset,
  };
}

const collapsedWrap: CSSProperties = {
  display: "block",
  padding: 0,
  background: "transparent",
  opacity: 0.5,
  pointerEvents: "none",
};

const expandedWrap: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 16px 8px 12px",
  borderRadius: 999,
  // Deliberately low-contrast: legible to someone who looks at it, not an
  // element that competes with the room's welcome message.
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "rgba(255,255,255,0.78)",
  opacity: 0.9,
  pointerEvents: "none",
};

const dot: CSSProperties = {
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: "#e0a53d",
  boxShadow: "0 0 10px rgba(224,165,61,0.7)",
  animation: "pulseGlow 3.2s ease-in-out infinite",
};

const text: CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
  letterSpacing: "0.01em",
  whiteSpace: "nowrap",
};
